// frontend/src/components/landing/parts.js
import React, { useRef } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, ButtonBase } from '@mui/material';
import { keyframes } from '@mui/system';
import useReveal from './useReveal';
import { ArrowRightIcon } from './icons';
import { GRAIN, tokens } from './landingTheme';

export const REDUCED_MOTION = '@media (prefers-reduced-motion: reduce)';

// Max 1200px content column with the page gutters
export const Container = ({ children, sx, ...rest }) => (
    <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 2, sm: 3, md: 4 }, ...sx }} {...rest}>
        {children}
    </Box>
);

// A page section: 80px vertical rhythm on phones, 144px on desktop; anchors clear the sticky header
export const Section = ({ children, sx, ...rest }) => (
    <Box
        component="section"
        sx={{ py: { xs: 10, md: 18 }, scrollMarginTop: 72, position: 'relative', ...sx }}
        {...rest}
    >
        {children}
    </Box>
);

// Fades its content up once scrolled into view; `delay` (ms) staggers siblings
export const Reveal = ({ children, delay = 0, sx, ...rest }) => {
    const [ref, shown] = useReveal();
    return (
        <Box
            ref={ref}
            sx={{
                opacity: shown ? 1 : 0,
                transform: shown ? 'none' : 'translateY(16px)',
                transition: `opacity 700ms ${tokens.ease} ${delay}ms, transform 700ms ${tokens.ease} ${delay}ms`,
                [REDUCED_MOTION]: { opacity: 1, transform: 'none', transition: 'none' },
                ...sx,
            }}
            {...rest}
        >
            {children}
        </Box>
    );
};

// Style for one item of a staggered group: hidden until `shown`, then in after `delay` ms
export const staggerItem = (shown, delay, from = 'translateY(12px)') => ({
    opacity: shown ? 1 : 0,
    transform: shown ? 'none' : from,
    transition: `opacity 500ms ${tokens.ease} ${delay}ms, transform 500ms ${tokens.ease} ${delay}ms`,
    [REDUCED_MOTION]: { opacity: 1, transform: 'none', transition: 'none' },
});

const focusRing = (color) => ({
    '&:focus-visible, &.Mui-focusVisible': {
        outline: `2px solid ${color}`,
        outlineOffset: 4,
    },
});

// Pressable buttons: a thin dark outline and a solid shadow offset to the corner; on hover the
// button presses halfway into its shadow, and all the way while clicked
const BUTTON_STYLES = {
    primary: {
        bgcolor: tokens.accent,
        color: '#FFFFFF',
        borderColor: tokens.ink,
        '--press': tokens.highlight,
        ...focusRing(tokens.accent),
    },
    secondary: {
        bgcolor: '#FFFFFF',
        color: tokens.ink,
        borderColor: tokens.ink,
        '--press': '#D6E0EC',
        ...focusRing(tokens.accent),
    },
    // White on the navy closing band
    inverse: {
        bgcolor: '#FFFFFF',
        color: tokens.accent,
        borderColor: '#FFFFFF',
        '--press': tokens.highlight,
        ...focusRing('#FFFFFF'),
    },
};

// Button that is a router link (`to`) or a plain link (`href`); `arrow` adds an arrow that nudges on hover
export const LandingButton = ({ variant = 'primary', size = 'large', arrow = false, to, href, children, sx, ...rest }) => {
    const linkProps = to ? { component: RouterLink, to } : { component: 'a', href };
    const large = size === 'large';
    const depth = large ? 4 : 3;
    return (
        <ButtonBase
            {...linkProps}
            disableRipple
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1.25,
                border: '1.5px solid',
                borderRadius: large ? 3.5 : 3,
                px: large ? 3 : 2,
                minHeight: large ? 52 : 40,
                fontFamily: tokens.body,
                fontSize: large ? '1rem' : '0.875rem',
                fontWeight: 600,
                letterSpacing: '-0.01em',
                whiteSpace: 'nowrap',
                boxShadow: `${depth}px ${depth}px 0 var(--press)`,
                transition: 'transform 160ms ease-out, box-shadow 160ms ease-out',
                '& svg': { transition: 'transform 200ms ease-out' },
                '&:hover': {
                    transform: `translate(${depth / 2}px, ${depth / 2}px)`,
                    boxShadow: `${depth / 2}px ${depth / 2}px 0 var(--press)`,
                },
                '&:hover svg': { transform: 'translateX(3px)' },
                '&:active': { transform: `translate(${depth}px, ${depth}px)`, boxShadow: '0 0 0 var(--press)' },
                [REDUCED_MOTION]: { transition: 'none', '&:hover svg': { transform: 'none' } },
                ...BUTTON_STYLES[variant],
                ...sx,
            }}
            {...rest}
        >
            {children}
            {arrow && <ArrowRightIcon size={large ? 18 : 16} />}
        </ButtonBase>
    );
};

// Small rounded label
export const Chip = ({ children, sx }) => (
    <Box
        component="span"
        sx={{
            display: 'inline-flex',
            alignItems: 'center',
            px: 1.5,
            py: 0.5,
            borderRadius: 999,
            fontSize: '0.8125rem',
            fontWeight: 500,
            lineHeight: 1.4,
            bgcolor: tokens.accentTint,
            color: tokens.accent,
            ...sx,
        }}
    >
        {children}
    </Box>
);

// Card that tilts gently towards the pointer; on hover a navy-to-peach bar draws across its top and
// any `.card-tile` inside turns peach. Still on touch and with reduced motion.
export const TiltCard = ({ children, sx, ...rest }) => {
    const ref = useRef(null);

    const handleMove = (event) => {
        const node = ref.current;
        if (!node || !window.matchMedia) return;
        if (window.matchMedia('(prefers-reduced-motion: reduce), (pointer: coarse)').matches) return;
        const rect = node.getBoundingClientRect();
        const x = (event.clientX - rect.left) / rect.width;
        const y = (event.clientY - rect.top) / rect.height;
        node.style.setProperty('--rx', `${(0.5 - y) * 3}deg`);
        node.style.setProperty('--ry', `${(x - 0.5) * 3}deg`);
    };

    const handleLeave = () => {
        const node = ref.current;
        if (!node) return;
        node.style.setProperty('--rx', '0deg');
        node.style.setProperty('--ry', '0deg');
    };

    return (
        <Box
            ref={ref}
            onMouseMove={handleMove}
            onMouseLeave={handleLeave}
            sx={{
                position: 'relative',
                bgcolor: tokens.surface,
                border: `1px solid ${tokens.line}`,
                borderRadius: 6,
                boxShadow: tokens.shadow,
                overflow: 'hidden',
                transform: 'perspective(1200px) rotateX(var(--rx, 0deg)) rotateY(var(--ry, 0deg))',
                transition: 'transform 200ms ease-out, box-shadow 240ms ease-out',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    width: '100%',
                    height: 3,
                    background: `linear-gradient(90deg, ${tokens.accent}, ${tokens.highlight})`,
                    transform: 'scaleX(0)',
                    transformOrigin: 'left',
                    transition: `transform 320ms ${tokens.ease}`,
                },
                '& .card-tile': { transition: 'background-color 240ms ease-out, color 240ms ease-out' },
                '&:hover': { boxShadow: tokens.shadowLift },
                '&:hover::before': { transform: 'scaleX(1)' },
                '&:hover .card-tile': { bgcolor: tokens.highlightTint, color: '#B4531F' },
                [REDUCED_MOTION]: { transform: 'none', transition: 'none', '&::before': { transition: 'none' } },
                ...sx,
            }}
            {...rest}
        >
            {children}
        </Box>
    );
};

const drift = keyframes`
    0%   { transform: translate3d(-3%, 0, 0) scale(1); }
    100% { transform: translate3d(3%, -2%, 0) scale(1.08); }
`;

// One soft glow of the accent (with a touch of peach) behind the content, under a light grain.
// `dark` is for the navy band.
export const Glow = ({ dark = false, fade = false }) => (
    <>
        <Box
            aria-hidden="true"
            sx={{
                position: 'absolute',
                left: '50%',
                top: fade ? '38%' : '50%',
                width: { xs: 520, md: 900 },
                height: { xs: 360, md: 520 },
                ml: { xs: '-260px', md: '-450px' },
                mt: { xs: '-180px', md: '-260px' },
                borderRadius: '50%',
                background: dark
                    ? 'radial-gradient(closest-side, rgba(245, 158, 108, 0.30), rgba(58, 90, 133, 0.35) 55%, transparent)'
                    : 'radial-gradient(closest-side, rgba(58, 90, 133, 0.16), rgba(245, 158, 108, 0.10) 60%, transparent)',
                filter: 'blur(20px)',
                animation: `${drift} 18s ease-in-out infinite alternate`,
                [REDUCED_MOTION]: { animation: 'none' },
            }}
        />
        <Box
            aria-hidden="true"
            sx={{ position: 'absolute', inset: 0, backgroundImage: GRAIN, opacity: dark ? 0.08 : 0.04, mixBlendMode: dark ? 'overlay' : 'multiply' }}
        />
    </>
);
